import { sanitizeOfficialSources } from "./source-policy";
import {
  clearConversation,
  loadConversation,
  saveConversation,
} from "./storage";
import { WIDGET_STYLES } from "./styles";
import { hasCompleteLead, requestHandoff, streamChat } from "./transport";
import type {
  NormalizedWidgetConfig,
  WidgetApi,
  WidgetBuildMeta,
  WidgetConfig,
  WidgetGlobal,
  WidgetMessage,
  WidgetRuntime,
  WidgetTelemetryEvent,
} from "./types";

export type { WidgetBuildMeta, WidgetTelemetryEvent } from "./types";

const DEFAULT_CONFIG: NormalizedWidgetConfig = {
  position: "bottom-right",
  zIndex: 2_147_483_000,
  persistence: "local-7-days",
  backend: {
    baseUrl: "",
    responseMode: "concise",
    sourceCount: "all",
    firstEventTimeoutMs: 16_000,
    requestTimeoutMs: 45_000,
  },
  ui: {
    title: "LPU DE Student Support",
    subtitle: "Online now",
    placeholder: "Ask us anything…",
    welcomeMessage:
      "Hi, I’m your LPU DE support assistant. How can I help with programmes, fees, eligibility or admissions today?",
    cssVars: {},
  },
  lead: {
    name: "",
    email: "",
    mobile: "",
    programInterested: "",
  },
  handoff: {
    enabled: true,
    label: "Talk to an admission counsellor",
    timeoutMs: 4_000,
  },
};

const ALLOWED_CSS_VARS = new Set([
  "--lpu-navy",
  "--lpu-yellow",
  "--lpu-ink",
  "--lpu-muted",
  "--lpu-line",
  "--lpu-surface",
  "--lpu-soft",
  "--lpu-shadow",
]);

/**
 * Creates the stable public widget API around one minimal chat runtime.
 */
export function createWidgetApi(
  global: WidgetGlobal,
  buildMeta: WidgetBuildMeta,
): WidgetApi {
  let runtime: WidgetRuntime | null = null;
  const observers = new Set<(event: WidgetTelemetryEvent) => void>();

  /**
   * Emits a small in-memory diagnostic event without external telemetry.
   */
  const emit = (type: string, detail: Record<string, unknown> = {}): void => {
    const event: WidgetTelemetryEvent = {
      type,
      detail,
      timestamp: Date.now(),
      version: buildMeta.version,
    };
    for (const observer of observers) {
      observer(event);
    }
  };

  const api: WidgetApi = {
    init(config: WidgetConfig = {}): WidgetApi {
      if (runtime) {
        return api;
      }
      if (!global.document?.body) {
        return api;
      }
      runtime = mountWidget(global.document, normalizeConfig(config), emit);
      emit("initialized");
      return api;
    },

    open(): WidgetApi {
      if (!runtime) {
        api.init();
      }
      if (runtime) {
        setOpen(runtime, true);
        emit("opened");
      }
      return api;
    },

    close(): WidgetApi {
      if (runtime) {
        setOpen(runtime, false);
        emit("closed");
      }
      return api;
    },

    destroy(): WidgetApi {
      if (!runtime) {
        return api;
      }
      runtime.abortController?.abort();
      runtime.inactivityController?.destroy();
      if (runtime.clearConfirmationTimer) {
        clearTimeout(runtime.clearConfirmationTimer);
      }
      for (const cleanup of runtime.cleanup) {
        cleanup();
      }
      runtime.host.remove();
      runtime = null;
      emit("destroyed");
      return api;
    },

    version: buildMeta.version,
    apiVersion: buildMeta.apiVersion,
    __isLPUWidget: true,
    __internal: {
      observe(listener): () => void {
        observers.add(listener);
        return () => observers.delete(listener);
      },
    },
  };

  return api;
}

/**
 * Registers the widget once and preserves the first loaded bundle.
 */
export function registerWidgetGlobal(
  global: WidgetGlobal,
  buildMeta: WidgetBuildMeta,
): WidgetApi {
  if (global.LPUWidget?.__isLPUWidget) {
    return global.LPUWidget;
  }
  const api = createWidgetApi(global, buildMeta);
  global.LPUWidget = api;
  return api;
}

/**
 * Normalizes public configuration while keeping the surface intentionally
 * narrow and rejecting arbitrary CSS custom properties.
 */
function normalizeConfig(config: WidgetConfig): NormalizedWidgetConfig {
  const backend = config.backend ?? {};
  const ui = config.ui ?? {};
  const lead = config.lead ?? {};
  const handoff = config.handoff ?? {};
  const cssVars: Record<string, string> = {};
  for (const [name, value] of Object.entries(ui.cssVars ?? {})) {
    if (ALLOWED_CSS_VARS.has(name) && value.trim()) {
      cssVars[name] = value.trim().slice(0, 180);
    }
  }

  return {
    position:
      config.position === "bottom-left" ? "bottom-left" : "bottom-right",
    persistence:
      config.persistence === "none" ? "none" : DEFAULT_CONFIG.persistence,
    zIndex: Number.isFinite(config.zIndex)
      ? Math.max(
          1,
          Math.min(config.zIndex ?? DEFAULT_CONFIG.zIndex, 2_147_483_647),
        )
      : DEFAULT_CONFIG.zIndex,
    backend: {
      baseUrl: trimTrailingSlash(
        backend.baseUrl?.trim() || DEFAULT_CONFIG.backend.baseUrl,
      ),
      responseMode: backend.responseMode === "blunt" ? "blunt" : "concise",
      sourceCount:
        backend.sourceCount === "all" ||
        (typeof backend.sourceCount === "number" &&
          Number.isFinite(backend.sourceCount))
          ? backend.sourceCount
          : "all",
      firstEventTimeoutMs: boundedTimeout(
        backend.firstEventTimeoutMs,
        DEFAULT_CONFIG.backend.firstEventTimeoutMs,
        1_000,
        30_000,
      ),
      requestTimeoutMs: boundedTimeout(
        backend.requestTimeoutMs,
        DEFAULT_CONFIG.backend.requestTimeoutMs,
        5_000,
        120_000,
      ),
    },
    ui: {
      title: cleanCopy(ui.title, DEFAULT_CONFIG.ui.title, 60),
      subtitle: cleanCopy(ui.subtitle, DEFAULT_CONFIG.ui.subtitle, 80),
      placeholder: cleanCopy(
        ui.placeholder,
        DEFAULT_CONFIG.ui.placeholder,
        100,
      ),
      welcomeMessage: cleanCopy(
        ui.welcomeMessage,
        DEFAULT_CONFIG.ui.welcomeMessage,
        300,
      ),
      cssVars,
    },
    lead: {
      name: cleanCopy(lead.name, "", 80),
      email: cleanCopy(lead.email, "", 254),
      mobile: cleanCopy(lead.mobile, "", 24),
      programInterested: cleanCopy(lead.programInterested, "", 120),
    },
    handoff: {
      enabled: handoff.enabled !== false,
      label: cleanCopy(handoff.label, DEFAULT_CONFIG.handoff.label, 80),
      timeoutMs: boundedTimeout(
        handoff.timeoutMs,
        DEFAULT_CONFIG.handoff.timeoutMs,
        1_000,
        15_000,
      ),
    },
  };
}

/**
 * Mounts the complete chat surface into an isolated shadow root.
 */
function mountWidget(
  document: Document,
  config: NormalizedWidgetConfig,
  emit: (type: string, detail?: Record<string, unknown>) => void,
): WidgetRuntime {
  const host = document.createElement("div");
  host.dataset.lpuChatHost = "";
  const shadow = host.attachShadow({ mode: "open" });
  const style = document.createElement("style");
  style.textContent = WIDGET_STYLES;
  const root = document.createElement("div");
  root.className = "lpu-chat-root";
  root.dataset.open = "false";
  root.dataset.position = config.position;
  root.style.setProperty("--lpu-z", String(config.zIndex));
  for (const [name, value] of Object.entries(config.ui.cssVars)) {
    root.style.setProperty(name, value);
  }
  root.innerHTML = shellMarkup(config);
  shadow.append(style, root);
  document.body.append(host);
  const storage =
    config.persistence === "none" ? null : resolveStorage(document);
  const restored = loadConversation(storage);

  const runtime: WidgetRuntime = {
    host,
    shadow,
    panel: requiredElement(root, ".lpu-chat-panel"),
    launcher: requiredElement(root, ".lpu-chat-launcher"),
    thread: requiredElement(root, ".lpu-chat-thread"),
    composer: requiredElement(root, ".lpu-chat-composer"),
    characterCounter: requiredElement(root, ".lpu-chat-character-count"),
    sendButton: requiredElement(root, ".lpu-chat-send"),
    status: requiredElement(root, ".lpu-chat-subtitle"),
    config,
    storage,
    messages: restored?.messages ?? [],
    sessionId: restored?.sessionId ?? createId(),
    open: false,
    busy: false,
    handoffStatus: "idle",
    abortController: null,
    clearConfirmationTimer: null,
    inactivityController: null,
    cleanup: [],
  };

  if (runtime.messages.length > 0) {
    const latestMessageIndex = runtime.messages.length - 1;
    for (const [index, message] of runtime.messages.entries()) {
      const row = appendMessage(runtime, message);
      appendSources(runtime, row, message.sources);
      if (index === latestMessageIndex) {
        appendSuggestedQuestions(runtime, row, message.suggestions, emit);
      }
    }
  } else {
    const welcome: WidgetMessage = {
      id: createId(),
      role: "bot",
      content: buildWelcomeMessage(config),
      status: "complete",
    };
    runtime.messages.push(welcome);
    appendMessage(runtime, welcome);
  }
  persistConversation(runtime);
  bindEvents(runtime, emit);
  return runtime;
}

/**
 * Returns the static, trusted shell markup. User and backend text are always
 * inserted separately through textContent.
 */
function shellMarkup(config: NormalizedWidgetConfig): string {
  return `
    <button class="lpu-chat-launcher" type="button" aria-label="Open LPU DE chat" aria-expanded="false">
      <span class="lpu-chat-launcher-icon"><svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 18.5 3.5 21l1-4A8.5 8.5 0 1 1 7 18.5Z"/><path d="M8 10h8M8 14h5"/></svg></span>
      <span class="lpu-chat-launcher-copy"><strong>Ask LPU DE</strong><small>We’re online</small></span>
    </button>
    <section class="lpu-chat-panel" role="dialog" aria-labelledby="lpu-chat-title" aria-describedby="lpu-chat-privacy" aria-hidden="true">
      <header class="lpu-chat-header">
        <div class="lpu-chat-mark" aria-hidden="true"><span>DE</span><i></i></div>
        <div class="lpu-chat-heading">
          <h1 id="lpu-chat-title" class="lpu-chat-title">${escapeHtml(config.ui.title)}</h1>
          <p class="lpu-chat-subtitle" role="status" aria-live="polite"><span class="lpu-chat-status-dot"></span><span>${escapeHtml(config.ui.subtitle)}</span></p>
        </div>
        <button class="lpu-chat-clear" type="button" aria-label="Clear conversation" title="Clear conversation">
          <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 7h16M9 7V4h6v3M7 7l1 13h8l1-13M10 11v5M14 11v5"/></svg>
        </button>
        <button class="lpu-chat-close" type="button" aria-label="Close chat">
          <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2"><path d="m6 6 12 12M18 6 6 18"/></svg>
        </button>
      </header>
      <div class="lpu-chat-thread" role="log" aria-live="polite" aria-relevant="additions text" aria-busy="false"></div>
      <footer class="lpu-chat-composer-shell">
        <div class="lpu-chat-composer-row">
          <textarea class="lpu-chat-composer" rows="1" aria-label="Message LPU DE Student Support" aria-describedby="lpu-chat-privacy lpu-chat-character-count" placeholder="${escapeAttribute(config.ui.placeholder)}"></textarea>
          <button class="lpu-chat-send" type="button" aria-label="Send message" disabled>
            <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.2"><path d="m4 4 17 8-17 8 3-8-3-8Z"/><path d="M7 12h14"/></svg>
          </button>
        </div>
        <div class="lpu-chat-composer-meta">
          <p id="lpu-chat-privacy" class="lpu-chat-note">${
            config.persistence === "none"
              ? "This chat is not saved on this device"
              : "Saved on this device for 7 days · Clear anytime"
          }</p>
          <span id="lpu-chat-character-count" class="lpu-chat-character-count" aria-live="polite" hidden>0/300</span>
        </div>
      </footer>
    </section>`;
}

/**
 * Binds the small set of interactions needed by the single chat surface.
 */
function bindEvents(
  runtime: WidgetRuntime,
  emit: (type: string, detail?: Record<string, unknown>) => void,
): void {
  const closeButton = requiredElement<HTMLButtonElement>(
    runtime.shadow,
    ".lpu-chat-close",
  );
  const clearButton = requiredElement<HTMLButtonElement>(
    runtime.shadow,
    ".lpu-chat-clear",
  );
  const onLauncher = () => setOpen(runtime, true);
  const onClose = () => setOpen(runtime, false);
  const onClear = () => {
    if (clearButton.dataset.confirm !== "true") {
      requestClearConfirmation(runtime, clearButton);
      return;
    }
    cancelClearConfirmation(runtime, clearButton);
    resetConversation(runtime);
    emit("conversation_cleared");
  };
  const onInput = () => {
    updateCharacterCounter(runtime);
    resizeComposer(runtime.composer);
    const overLimit = countCharacters(runtime.composer.value) > 300;
    runtime.sendButton.disabled =
      runtime.busy ||
      runtime.handoffStatus === "connected" ||
      runtime.composer.value.trim().length === 0 ||
      overLimit;
  };
  const onSend = () => void sendCurrentMessage(runtime, emit);
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      void sendCurrentMessage(runtime, emit);
    } else if (event.key === "Escape") {
      setOpen(runtime, false);
    }
  };

  runtime.launcher.addEventListener("click", onLauncher);
  closeButton.addEventListener("click", onClose);
  clearButton.addEventListener("click", onClear);
  runtime.composer.addEventListener("input", onInput);
  runtime.composer.addEventListener("keydown", onKeyDown);
  runtime.sendButton.addEventListener("click", onSend);
  runtime.cleanup.push(
    () => runtime.launcher.removeEventListener("click", onLauncher),
    () => closeButton.removeEventListener("click", onClose),
    () => clearButton.removeEventListener("click", onClear),
    () => runtime.composer.removeEventListener("input", onInput),
    () => runtime.composer.removeEventListener("keydown", onKeyDown),
    () => runtime.sendButton.removeEventListener("click", onSend),
  );
}

/**
 * Arms a short, accessible second-click confirmation before deleting history.
 */
function requestClearConfirmation(
  runtime: WidgetRuntime,
  button: HTMLButtonElement,
): void {
  cancelClearConfirmation(runtime, button);
  button.dataset.confirm = "true";
  button.setAttribute("aria-label", "Confirm clear conversation");
  button.title = "Click again to clear conversation";
  setStatusLabel(runtime, "Click clear again to remove this chat");
  runtime.clearConfirmationTimer = setTimeout(() => {
    cancelClearConfirmation(runtime, button);
  }, 4_000);
}

/**
 * Restores the clear control and connection label after confirmation expires.
 */
function cancelClearConfirmation(
  runtime: WidgetRuntime,
  button: HTMLButtonElement,
): void {
  if (runtime.clearConfirmationTimer) {
    clearTimeout(runtime.clearConfirmationTimer);
    runtime.clearConfirmationTimer = null;
  }
  delete button.dataset.confirm;
  button.setAttribute("aria-label", "Clear conversation");
  button.title = "Clear conversation";
  setConnectionState(runtime, false);
}

/**
 * Sends the current input and streams one stable bot bubble in place.
 */
async function sendCurrentMessage(
  runtime: WidgetRuntime,
  emit: (type: string, detail?: Record<string, unknown>) => void,
  suggestionSelected = false,
): Promise<void> {
  const text = runtime.composer.value.trim();
  if (countCharacters(text) > 300) {
    updateCharacterCounter(runtime);
    setStatusLabel(runtime, "Shorten your message to 300 characters to send");
    return;
  }
  if (!text || runtime.busy || runtime.handoffStatus === "connected") {
    return;
  }

  runtime.inactivityController?.userSent();
  runtime.shadow.querySelectorAll(".lpu-chat-suggestions").forEach((list) => {
    list.remove();
  });
  const history = [...runtime.messages];
  const userMessage: WidgetMessage = {
    id: createId(),
    role: "user",
    content: text,
  };
  runtime.messages.push(userMessage);
  const userNode = appendMessage(runtime, userMessage);
  persistConversation(runtime);
  runtime.composer.value = "";
  updateCharacterCounter(runtime);
  resizeComposer(runtime.composer);
  setBusy(runtime, true);

  const botMessage: WidgetMessage = {
    id: createId(),
    role: "bot",
    content: "",
  };
  runtime.messages.push(botMessage);
  const botNode = appendTypingMessage(runtime, botMessage);
  const bubble = requiredElement<HTMLElement>(botNode, ".lpu-chat-bubble");
  const abortController = new AbortController();
  runtime.abortController = abortController;
  let pendingText = "";
  let frame = 0;

  /**
   * Flushes accumulated stream chunks once per animation frame to avoid a DOM
   * write and scroll operation for every model token.
   */
  const flush = (): void => {
    frame = 0;
    if (!pendingText) {
      return;
    }
    botMessage.content += pendingText;
    pendingText = "";
    bubble.textContent = botMessage.content;
    botNode.dataset.streaming = "true";
    scrollToLatest(runtime);
  };

  try {
    emit("message_sent", { length: text.length });
    const result = await streamChat(
      runtime.config,
      runtime.sessionId,
      text,
      history,
      abortController.signal,
      (token) => {
        pendingText += token;
        if (!frame) {
          frame = requestFrame(runtime, flush);
        }
      },
      suggestionSelected,
    );
    if (frame) {
      cancelFrame(runtime, frame);
    }
    flush();
    botNode.dataset.streaming = "false";
    if (!botMessage.content.trim()) {
      botMessage.content =
        "I could not produce an answer. Please try asking in a different way.";
      bubble.textContent = botMessage.content;
    }
    botMessage.status = "complete";
    botMessage.sources = result.sources;
    botMessage.suggestions = result.suggestions;
    appendSources(runtime, botNode, result.sources);
    appendSuggestedQuestions(runtime, botNode, result.suggestions, emit);
    if (result.handoff?.suggested) {
      appendHandoffAction(runtime, botNode, emit);
    }
    persistConversation(runtime);
    setConnectionState(runtime, false);
    emit("message_received", {
      length: botMessage.content.length,
      messageCount: 1,
      cacheHit: result.cacheHit,
      transportRetries: result.transportRetries,
    });
    runtime.inactivityController?.assistantResponded();
  } catch (error) {
    if (frame) {
      cancelFrame(runtime, frame);
      frame = 0;
    }
    if (abortController.signal.aborted) {
      pendingText = "";
      return;
    }
    flush();
    if (!botMessage.content.trim()) {
      botMessage.content =
        "I’m having trouble connecting right now. Please try again in a moment.";
      bubble.textContent = botMessage.content;
    }
    botMessage.status = "interrupted";
    botNode.dataset.streaming = "false";
    botNode.dataset.error = "true";
    appendRetryAction(runtime, botNode, () => {
      runtime.messages = runtime.messages.filter(
        (message) =>
          message.id !== userMessage.id && message.id !== botMessage.id,
      );
      userNode.remove();
      botNode.remove();
      runtime.composer.value = text;
      updateCharacterCounter(runtime);
      resizeComposer(runtime.composer);
      runtime.sendButton.disabled = false;
      void sendCurrentMessage(runtime, emit, true);
    });
    setConnectionState(runtime, true);
    persistConversation(runtime);
    emit("message_failed", {
      message: error instanceof Error ? error.message : String(error),
    });
  } finally {
    if (runtime.abortController === abortController) {
      runtime.abortController = null;
      setBusy(runtime, false);
      runtime.composer.focus({ preventScroll: true });
    }
  }
}

/** Renders contextual quick questions beneath the one complete answer. */
function appendSuggestedQuestions(
  runtime: WidgetRuntime,
  row: HTMLElement,
  suggestions: readonly string[] | undefined,
  emit: (type: string, detail?: Record<string, unknown>) => void,
): void {
  if (!suggestions?.length) return;
  const list = runtime.host.ownerDocument.createElement("div");
  list.className = "lpu-chat-suggestions";
  for (const question of suggestions.slice(0, 2)) {
    const clean = question.replace(/\s+/g, " ").trim().slice(0, 100);
    if (!clean) continue;
    const button = runtime.host.ownerDocument.createElement("button");
    button.type = "button";
    button.className = "lpu-chat-suggestion";
    button.textContent = clean;
    button.addEventListener("click", () => {
      if (runtime.busy || runtime.handoffStatus === "connected") return;
      list.remove();
      runtime.composer.value = clean;
      updateCharacterCounter(runtime);
      resizeComposer(runtime.composer);
      void sendCurrentMessage(runtime, emit);
    });
    list.append(button);
  }
  if (list.childElementCount > 0) insertAnswerAttachment(row, list);
}

/** Renders a real human-support action whenever the backend suggests it. */
function appendHandoffAction(
  runtime: WidgetRuntime,
  row: HTMLElement,
  emit: (type: string, detail?: Record<string, unknown>) => void,
): void {
  const action = runtime.host.ownerDocument.createElement("div");
  action.className = "lpu-chat-handoff";
  const button = runtime.host.ownerDocument.createElement("button");
  button.type = "button";
  button.textContent =
    runtime.config.handoff.enabled && hasCompleteLead(runtime.config)
      ? runtime.config.handoff.label
      : "Human support options";
  button.addEventListener(
    "click",
    () => void beginHandoff(runtime, action, button, emit),
    { once: true },
  );
  action.append(button);
  insertAnswerAttachment(row, action);
}

/** Attempts one user-consented handoff and keeps AI available on any fallback. */
async function beginHandoff(
  runtime: WidgetRuntime,
  action: HTMLElement,
  button: HTMLButtonElement,
  emit: (type: string, detail?: Record<string, unknown>) => void,
): Promise<void> {
  if (runtime.handoffStatus !== "idle") return;
  if (!runtime.config.handoff.enabled || !hasCompleteLead(runtime.config)) {
    action.remove();
    const notice: WidgetMessage = {
      id: createId(),
      role: "bot",
      content:
        "No counsellor is available in this chat right now. You can continue here or use the official LPU DE contact options.",
    };
    appendMessage(runtime, notice);
    emit("handoff_result", { status: "unavailable", reason: "lead_missing" });
    return;
  }
  runtime.handoffStatus = "checking";
  button.disabled = true;
  button.textContent = "Checking counsellor availability…";
  setBusy(runtime, true);
  setStatusLabel(runtime, "Connecting to a counsellor");
  const controller = new AbortController();
  runtime.abortController = controller;
  const result = await requestHandoff(
    runtime.config,
    runtime.sessionId,
    runtime.messages,
    controller.signal,
  );
  if (runtime.abortController === controller) runtime.abortController = null;
  if (controller.signal.aborted) return;
  action.remove();

  const notice: WidgetMessage = {
    id: createId(),
    role: "bot",
    content: handoffNotice(result.status, result.agentName),
  };
  const noticeRow = appendMessage(runtime, notice);
  if (result.status === "connected") {
    runtime.handoffStatus = "connected";
    appendConnectedActions(runtime, noticeRow, result.connectionUrl, emit);
    setStatusLabel(runtime, "Connected to a counsellor");
  } else {
    runtime.handoffStatus = "idle";
    setConnectionState(runtime, false);
  }
  setBusy(runtime, false);
  emit("handoff_result", { status: result.status });
}

/** Produces a short, non-technical handoff outcome for the conversation. */
function handoffNotice(
  status: "busy" | "connected" | "unavailable",
  agentName?: string,
): string {
  if (status === "connected") {
    return agentName
      ? `${agentName} is available. You’re connected to an admission counsellor.`
      : "You’re connected to an admission counsellor.";
  }
  if (status === "busy") {
    return "Our counsellors are busy right now. I’m still here and can continue helping you.";
  }
  return "Live support is unavailable right now. I’m still here and can continue helping you.";
}

/** Adds safe connection navigation and an explicit return-to-AI control. */
function appendConnectedActions(
  runtime: WidgetRuntime,
  row: HTMLElement,
  connectionUrl: string | undefined,
  emit: (type: string, detail?: Record<string, unknown>) => void,
): void {
  const actions = runtime.host.ownerDocument.createElement("div");
  actions.className = "lpu-chat-handoff lpu-chat-handoff--connected";
  if (connectionUrl) {
    const link = runtime.host.ownerDocument.createElement("a");
    link.href = connectionUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.textContent = "Open live chat";
    actions.append(link);
  }
  const resume = runtime.host.ownerDocument.createElement("button");
  resume.type = "button";
  resume.textContent = "Continue with AI";
  resume.addEventListener(
    "click",
    () => {
      runtime.handoffStatus = "idle";
      actions.remove();
      setBusy(runtime, false);
      setConnectionState(runtime, false);
      runtime.composer.focus({ preventScroll: true });
      emit("handoff_resumed_ai");
    },
    { once: true },
  );
  actions.append(resume);
  row.after(actions);
}

/**
 * Adds an explicit one-click retry beside an interrupted response.
 */
function appendRetryAction(
  runtime: WidgetRuntime,
  row: HTMLElement,
  retry: () => void,
): void {
  const action = runtime.host.ownerDocument.createElement("div");
  action.className = "lpu-chat-retry";
  const note = runtime.host.ownerDocument.createElement("span");
  note.textContent = "Response interrupted.";
  const button = runtime.host.ownerDocument.createElement("button");
  button.type = "button";
  button.textContent = "Try again";
  button.addEventListener(
    "click",
    () => {
      action.remove();
      retry();
    },
    { once: true },
  );
  action.append(note, button);
  row.after(action);
}

/**
 * Adds a complete message bubble using textContent for XSS safety.
 */
function appendMessage(
  runtime: WidgetRuntime,
  message: WidgetMessage,
): HTMLElement {
  const row = runtime.host.ownerDocument.createElement("div");
  row.className = "lpu-chat-message";
  row.dataset.role = message.role;
  if (message.role === "bot") {
    const avatar = runtime.host.ownerDocument.createElement("span");
    avatar.className = "lpu-chat-message-avatar";
    avatar.setAttribute("aria-hidden", "true");
    avatar.textContent = "DE";
    row.append(avatar);
  }
  const bubble = runtime.host.ownerDocument.createElement("div");
  bubble.className = "lpu-chat-bubble";
  bubble.textContent = message.content;
  row.append(bubble);
  runtime.thread.append(row);
  scrollToLatest(runtime);
  return row;
}

/**
 * Adds a reserved bot row with a typing indicator to prevent geometry changes
 * between waiting and streaming states.
 */
function appendTypingMessage(
  runtime: WidgetRuntime,
  message: WidgetMessage,
): HTMLElement {
  const row = appendMessage(runtime, message);
  row.dataset.streaming = "true";
  const bubble = requiredElement<HTMLElement>(row, ".lpu-chat-bubble");
  const typing = runtime.host.ownerDocument.createElement("span");
  typing.className = "lpu-chat-typing";
  typing.setAttribute("aria-label", "LPU DE support is typing");
  typing.innerHTML = "<i></i><i></i><i></i>";
  bubble.append(typing);
  return row;
}

/**
 * Adds a bounded set of precise official source links beneath an answer.
 */
function appendSources(
  runtime: WidgetRuntime,
  row: HTMLElement,
  sources: WidgetMessage["sources"],
): void {
  const safeSources = sanitizeOfficialSources(sources ?? []);
  if (!safeSources.length) {
    return;
  }
  const list = runtime.host.ownerDocument.createElement("div");
  list.className = "lpu-chat-sources";
  for (const source of safeSources) {
    const link = runtime.host.ownerDocument.createElement("a");
    link.className = "lpu-chat-source";
    link.href = source.url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.textContent = source.title ?? "Official LPU DE page";
    list.append(link);
  }
  insertAnswerAttachment(row, list);
}

/** Keeps sources, quick questions and handoff controls in one answer group. */
function insertAnswerAttachment(
  row: HTMLElement,
  attachment: HTMLElement,
): void {
  let anchor = row;
  while (
    anchor.nextElementSibling?.classList.contains("lpu-chat-sources") ||
    anchor.nextElementSibling?.classList.contains("lpu-chat-suggestions") ||
    anchor.nextElementSibling?.classList.contains("lpu-chat-handoff")
  ) {
    anchor = anchor.nextElementSibling as HTMLElement;
  }
  anchor.after(attachment);
}

/**
 * Clears local history, rotates the anonymous session ID, and restores welcome.
 */
function resetConversation(runtime: WidgetRuntime): void {
  runtime.abortController?.abort();
  runtime.inactivityController?.userSent();
  clearConversation(runtime.storage);
  runtime.messages.length = 0;
  runtime.sessionId = createId();
  runtime.thread.replaceChildren();
  const welcome: WidgetMessage = {
    id: createId(),
    role: "bot",
    content: buildWelcomeMessage(runtime.config),
    status: "complete",
  };
  runtime.messages.push(welcome);
  runtime.handoffStatus = "idle";
  appendMessage(runtime, welcome);
  setBusy(runtime, false);
  setConnectionState(runtime, false);
  persistConversation(runtime);
}

/**
 * Saves the bounded active transcript without making chat depend on storage.
 */
function persistConversation(runtime: WidgetRuntime): void {
  saveConversation(
    runtime.storage,
    runtime.sessionId,
    runtime.messages,
    Date.now(),
    runtime.inactivityController?.snapshot(),
  );
}

/**
 * Opens or closes the panel without remounting it, preserving the session.
 */
function setOpen(runtime: WidgetRuntime, open: boolean): void {
  runtime.open = open;
  const root = requiredElement<HTMLElement>(runtime.shadow, ".lpu-chat-root");
  root.dataset.open = String(open);
  runtime.panel.setAttribute("aria-hidden", String(!open));
  runtime.launcher.setAttribute("aria-expanded", String(open));
  if (open) {
    requestFrame(runtime, () =>
      runtime.composer.focus({ preventScroll: true }),
    );
  } else {
    runtime.launcher.focus({ preventScroll: true });
  }
}

/**
 * Updates the compact online/error indicator without shifting header content.
 */
function setConnectionState(runtime: WidgetRuntime, error: boolean): void {
  const dot = requiredElement<HTMLElement>(
    runtime.status,
    ".lpu-chat-status-dot",
  );
  dot.dataset.error = String(error);
  setStatusLabel(
    runtime,
    error ? "Connection issue" : runtime.config.ui.subtitle,
  );
}

/**
 * Announces a compact connection or privacy status without moving the header.
 */
function setStatusLabel(runtime: WidgetRuntime, message: string): void {
  const label = runtime.status.querySelector("span:last-child");
  if (label) {
    label.textContent = message;
  }
}

/**
 * Locks sending while one answer is in flight.
 */
function setBusy(runtime: WidgetRuntime, busy: boolean): void {
  runtime.busy = busy;
  runtime.thread.setAttribute("aria-busy", String(busy));
  const locked = busy || runtime.handoffStatus === "connected";
  runtime.composer.disabled = locked;
  runtime.sendButton.disabled =
    locked ||
    runtime.composer.value.trim().length === 0 ||
    countCharacters(runtime.composer.value) > 300;
}

/** Shows a quiet character count only as the 300-character limit approaches. */
function updateCharacterCounter(runtime: WidgetRuntime): void {
  const length = countCharacters(runtime.composer.value);
  const overLimit = length > 300;
  runtime.characterCounter.textContent = `${String(length)}/300`;
  runtime.characterCounter.hidden = length < 240;
  runtime.characterCounter.dataset.overLimit = String(overLimit);
  runtime.composer.setAttribute("aria-invalid", String(overLimit));
}

/** Counts user-visible Unicode code points without splitting surrogate pairs. */
function countCharacters(value: string): number {
  return Array.from(value).length;
}

/**
 * Resizes the composer inside its bounded height without moving the panel.
 */
function resizeComposer(composer: HTMLTextAreaElement): void {
  composer.style.height = "0px";
  composer.style.height = `${Math.min(composer.scrollHeight, 92)}px`;
}

/**
 * Keeps the latest content visible only within the widget scroll container.
 */
function scrollToLatest(runtime: WidgetRuntime): void {
  runtime.thread.scrollTop = runtime.thread.scrollHeight;
}

/**
 * Schedules visual work through the widget's own browser window.
 */
function requestFrame(
  runtime: WidgetRuntime,
  callback: FrameRequestCallback,
): number {
  const view = runtime.host.ownerDocument.defaultView;
  if (view?.requestAnimationFrame) {
    return Number(view.requestAnimationFrame(callback));
  }
  callback(Date.now());
  return 0;
}

/**
 * Cancels visual work through the widget's own browser window.
 */
function cancelFrame(runtime: WidgetRuntime, id: number): void {
  runtime.host.ownerDocument.defaultView?.cancelAnimationFrame?.(id);
}

/**
 * Resolves a required typed element or fails early during development.
 */
function requiredElement<T extends Element = HTMLElement>(
  root: ParentNode,
  selector: string,
): T {
  const element = root.querySelector<T>(selector);
  if (!element) {
    throw new Error(`Missing widget element: ${selector}`);
  }
  return element;
}

/**
 * Generates a session-local identifier without storing personal information.
 */
function createId(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  );
}

/**
 * Resolves localStorage defensively because privacy modes may throw on access.
 */
function resolveStorage(document: Document): Storage | null {
  try {
    return document.defaultView?.localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * Removes trailing slashes from a backend origin.
 */
function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, "");
}

/**
 * Clamps user-facing configuration copy to a safe plain string.
 */
function cleanCopy(
  value: string | undefined,
  fallback: string,
  maxLength: number,
): string {
  const clean = value?.replace(/\s+/g, " ").trim();
  return clean ? clean.slice(0, maxLength) : fallback;
}

/** Builds a warm, bounded welcome without exposing contact information. */
function buildWelcomeMessage(config: NormalizedWidgetConfig): string {
  const name = firstName(config.lead.name);
  const program = config.lead.programInterested;
  if (name && program) {
    return `Hi ${name}. I can help with ${program} fees, eligibility, admission, and course choices. What would you like to know?`.slice(
      0,
      150,
    );
  }
  if (name) {
    return `Hi ${name}. I can help with LPU DE programmes, fees, eligibility, and admission. What would you like to know?`;
  }
  if (program) {
    return `I can help with ${program} fees, eligibility, admission, and course choices. What would you like to know?`.slice(
      0,
      150,
    );
  }
  return config.ui.welcomeMessage;
}

/** Extracts one respectful display name from host-supplied lead context. */
function firstName(value: string): string {
  return (
    value
      .split(/\s+/)[0]
      ?.replace(/[^\p{L}'-]/gu, "")
      .slice(0, 30) ?? ""
  );
}

/**
 * Clamps a public timeout to an operationally safe range.
 */
function boundedTimeout(
  value: number | undefined,
  fallback: number,
  minimum: number,
  maximum: number,
): number {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.max(minimum, Math.min(value, maximum))
    : fallback;
}

/**
 * Escapes trusted shell text before static insertion.
 */
function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

/**
 * Escapes text used in an HTML attribute.
 */
function escapeAttribute(value: string): string {
  return escapeHtml(value).replaceAll('"', "&quot;").replaceAll("'", "&#39;");
}

export const RUNTIME_POLICY = Object.freeze({
  persistence: "configurable-local-or-none",
  leadCapture: false,
  feedback: false,
  maxHistoryMessages: 20,
});
